import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, Filter, Music, SlidersHorizontal, X, ArrowUpDown, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import BeatPlayer from "../components/BeatPlayer";
import RecommendedBeats from "../components/RecommendedBeats";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_COLORS = {
  Dark: "#6b21a8", Aggressive: "#dc2626", Bouncy: "#f59e0b", Melodic: "#ec4899",
  Chill: "#06b6d4", Hype: "#16a34a", Sad: "#3b82f6", Ethereal: "#8b5cf6"
};

const BeatsPage = () => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("all");
  const [selectedKey, setSelectedKey] = useState("all");
  const [selectedMood, setSelectedMood] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [bpmRange, setBpmRange] = useState([60, 200]);
  const [genres, setGenres] = useState([]);
  const [keys, setKeys] = useState([]);
  const [moods, setMoods] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [genresRes, keysRes, moodsRes] = await Promise.all([
          axios.get(`${API}/beats/genres/list`),
          axios.get(`${API}/beats/keys/list`),
          axios.get(`${API}/discover/moods`)
        ]);
        setGenres(genresRes.data.genres || []);
        setKeys(keysRes.data.keys || []);
        setMoods(moodsRes.data.moods || []);
      } catch (error) {
        console.error("Error fetching metadata:", error);
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    const fetchBeats = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedGenre !== "all") params.genre = selectedGenre;
        if (selectedKey !== "all") params.key = selectedKey;
        if (selectedMood !== "all") params.mood = selectedMood;
        if (searchQuery) params.search = searchQuery;
        if (bpmRange[0] > 60) params.bpm_min = bpmRange[0];
        if (bpmRange[1] < 200) params.bpm_max = bpmRange[1];
        if (sortBy !== "newest") params.sort_by = sortBy;
        
        const response = await axios.get(`${API}/beats`, { params });
        setBeats(response.data);
      } catch (error) {
        console.error("Error fetching beats:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchBeats, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, selectedGenre, selectedKey, selectedMood, bpmRange, sortBy]);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (selectedGenre !== "all") count++;
    if (selectedKey !== "all") count++;
    if (selectedMood !== "all") count++;
    if (bpmRange[0] > 60 || bpmRange[1] < 200) count++;
    setActiveFilters(count);
  }, [selectedGenre, selectedKey, selectedMood, bpmRange]);

  const clearFilters = () => {
    setSelectedGenre("all");
    setSelectedKey("all");
    setSelectedMood("all");
    setBpmRange([60, 200]);
    setSearchQuery("");
    setSortBy("newest");
  };

  return (
    <div className="min-h-screen py-10 px-6" data-testid="beats-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-4">
            Beat Store
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mb-4">
            Premium Beats
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            High-quality instrumentals ready for your next hit. Multiple licensing options available.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search beats by title or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/10 h-12"
              data-testid="beats-search"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-48 bg-black/50 border-white/10 h-12" data-testid="sort-filter">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className={`border-white/10 h-12 ${showFilters ? 'bg-neon-cyan/20 border-neon-cyan/50' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-button"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilters > 0 && (
              <span className="ml-2 w-5 h-5 bg-neon-cyan text-black text-xs font-bold rounded-full flex items-center justify-center">
                {activeFilters}
              </span>
            )}
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass p-6 mb-6"
            data-testid="filters-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm uppercase tracking-wide text-zinc-400">Advanced Filters</h3>
              {activeFilters > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 text-xs" data-testid="clear-filters-button">
                  <X className="w-3 h-3 mr-1" /> Clear All
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Genre Filter */}
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Genre</label>
                <Select value={selectedGenre} onValueChange={setSelectedGenre}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="genre-filter">
                    <SelectValue placeholder="All Genres" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="all">All Genres</SelectItem>
                    {genres.map((genre) => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Key Filter */}
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Key</label>
                <Select value={selectedKey} onValueChange={setSelectedKey}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="key-filter">
                    <SelectValue placeholder="All Keys" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="all">All Keys</SelectItem>
                    {keys.map((k) => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mood Filter */}
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">Mood</label>
                <Select value={selectedMood} onValueChange={setSelectedMood}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="mood-filter-select">
                    <SelectValue placeholder="All Moods" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="all">All Moods</SelectItem>
                    {moods.map((m) => (
                      <SelectItem key={m.name} value={m.name}>{m.name} ({m.beat_count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* BPM Range */}
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wide mb-2 block">
                  BPM Range: <span className="text-neon-cyan">{bpmRange[0]} - {bpmRange[1]}</span>
                </label>
                <Slider
                  value={bpmRange}
                  onValueChange={setBpmRange}
                  min={60}
                  max={200}
                  step={5}
                  className="mt-3"
                  data-testid="bpm-slider"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Mood Quick-Filter Chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="mood-chips">
          <button
            onClick={() => setSelectedMood("all")}
            className={`px-4 py-1.5 text-sm font-medium transition-all ${selectedMood === "all" ? "bg-neon-cyan text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10 border border-white/10"}`}
            data-testid="mood-chip-all"
          >
            All Moods
          </button>
          {moods.map(m => (
            <button
              key={m.name}
              onClick={() => setSelectedMood(m.name === selectedMood ? "all" : m.name)}
              className={`px-4 py-1.5 text-sm font-medium transition-all flex items-center gap-1.5 ${selectedMood === m.name ? "text-black" : "text-zinc-400 hover:text-white border border-white/10"}`}
              style={selectedMood === m.name ? { background: m.color } : { background: `${m.color}15` }}
              data-testid={`mood-chip-${m.name.toLowerCase()}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
              {m.name}
            </button>
          ))}
          <Link to="/discover" className="px-4 py-1.5 text-sm text-neon-magenta hover:text-neon-cyan transition-colors flex items-center gap-1" data-testid="discover-link">
            <Sparkles className="w-3 h-3" /> Discover
          </Link>
        </div>

        {/* License Info Banner */}
        <div className="glass p-6 mb-8">
          <h3 className="font-heading text-lg uppercase tracking-wide mb-4 text-neon-cyan">License Options</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-sm">
              <p className="font-mono text-neon-cyan text-xl font-bold">$29.99</p>
              <p className="text-sm text-zinc-400 uppercase tracking-wide">Basic</p>
              <p className="text-xs text-zinc-600 mt-1">MP3 Only</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-sm">
              <p className="font-mono text-neon-magenta text-xl font-bold">$79.99</p>
              <p className="text-sm text-zinc-400 uppercase tracking-wide">Premium</p>
              <p className="text-xs text-zinc-600 mt-1">WAV + Stems</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-sm">
              <p className="font-mono text-neon-green text-xl font-bold">$149.99</p>
              <p className="text-sm text-zinc-400 uppercase tracking-wide">Unlimited</p>
              <p className="text-xs text-zinc-600 mt-1">Unlimited Sales</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-sm border border-neon-yellow/30">
              <p className="font-mono text-neon-yellow text-xl font-bold">$499.99</p>
              <p className="text-sm text-zinc-400 uppercase tracking-wide">Exclusive</p>
              <p className="text-xs text-zinc-600 mt-1">Full Ownership</p>
            </div>
          </div>
        </div>

        {/* Recommended For You - only show when no active filters */}
        {activeFilters === 0 && selectedMood === "all" && !searchQuery && (
          <div className="mb-8" data-testid="beats-page-recommendations">
            <RecommendedBeats limit={6} compact />
          </div>
        )}

        {/* Results Count */}
        {!loading && (
          <p className="text-xs text-zinc-500 mb-4 uppercase tracking-wide" data-testid="results-count">
            {beats.length} beat{beats.length !== 1 ? 's' : ''} found
          </p>
        )}

        {/* Beats List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-zinc-900/50 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : beats.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">No beats found matching your criteria</p>
            <Button variant="outline" onClick={clearFilters} className="border-white/20">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {beats.map((beat, index) => (
              <motion.div
                key={beat.beat_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <BeatPlayer beat={beat} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeatsPage;
